import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { MipScenarioStepNumber } from '@/features/mip/mip-scenario/mip-scenario.interface';
import type { MipInvestmentPlanId, MipScenarioId } from '@/features/mip/shared/mip-ids.interface';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import { ACCESS_PROJECT_ADMINISTRATION, MIP_VIEWER_ACCESS } from '@/providers/rights.constants';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, type CanActivateChildFn, type CanActivateFn } from '@angular/router';
import { MipScenarioDataService } from '../mip-scenario/mip-scenario-data.service';

@Injectable({
    providedIn: 'root'
})
export class MipFeatureService {
    private _router = inject(Router);
    private _disabledFeaturesService = inject(DisabledFeaturesService);
    private _profileProvider = inject(ProfileProvider);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    goToFeature() {
        this._router.navigate(['/', 'mip']);
    }

    hasFeatureAccessR = computed(() => {
        const isFeatureEnabled = this._disabledFeaturesService.isFeatureEnabled(FeatureCode.MIP);
        const isMipViewer = this._profileProvider.hasRightR(MIP_VIEWER_ACCESS);
        const hideToItaly = this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_ITALY);

        return isFeatureEnabled && !hideToItaly && isMipViewer;
    });

    hasPremiumAccessR = computed(() => {
        const isFeatureEnabled = this._disabledFeaturesService.isFeatureEnabled(FeatureCode.MIP);
        const hasPremiumMIPModule = this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.MIP_PREMIUM);
        const isMipViewer = this._profileProvider.hasRightR(MIP_VIEWER_ACCESS);
        return isFeatureEnabled && hasPremiumMIPModule && isMipViewer;
    });

    private _hasFeatureAccess$ = toObservable(this.hasFeatureAccessR);

    hasFeatureAccess$() {
        return this._hasFeatureAccess$;
    }

    private async hasBaseAccess(): Promise<boolean> {
        if (!this._disabledFeaturesService.isFeatureEnabled(FeatureCode.MIP)) return false;
        let hasModuleAccess = await this._applicationModulesProvider.hasModuleAccess(ApplicationModuleCode.MIP);
        hasModuleAccess = true; //TODO : Temporary bypass for module access check until freemium features are implemented
        return hasModuleAccess;
    }

    async hasGeneralAccess(): Promise<boolean> {
        if (!(await this.hasBaseAccess())) return false;
        return (
            (await this._profileProvider.hasRight(ACCESS_PROJECT_ADMINISTRATION)) ||
            (await this._profileProvider.hasRight(MIP_VIEWER_ACCESS))
        );
    }

    async hasAdminAccess(): Promise<boolean> {
        if (!(await this.hasBaseAccess())) return false;
        return await this._profileProvider.hasRight(ACCESS_PROJECT_ADMINISTRATION);
    }
}

export const mipGuard: CanActivateFn & CanActivateChildFn = () => {
    const featureService = inject(MipFeatureService);
    return featureService.hasGeneralAccess();
};

export const mipAdminGuard: CanActivateFn = () => {
    const featureService = inject(MipFeatureService);
    return featureService.hasAdminAccess();
};

export const mipStepGuard: CanActivateFn = async route => {
    const mipScenarioDataService = inject(MipScenarioDataService);
    const router = inject(Router);

    const step = Number(route.params['step']);
    const investmentPlanId = Number(route.params['investmentPlanId']);
    if (isNaN(investmentPlanId) || isNaN(step)) {
        return router.navigate(['/mip']);
    }

    const scenarioIdParam = route.params['scenarioId'];
    const scenarioId = scenarioIdParam != null ? Number(scenarioIdParam) : null;
    if (scenarioIdParam != null) {
        const scenarioId = Number(scenarioIdParam);
        if (isNaN(scenarioId)) return router.navigate(['/mip']);
    }

    if (step === MipScenarioStepNumber.ADD_STANDARD_ACTIONS && scenarioId != null && !mipScenarioDataService.isGeneralInfoValid()) {
        const isValid = await mipScenarioDataService.checkGeneralInfoValid(
            investmentPlanId as MipInvestmentPlanId,
            scenarioId as MipScenarioId
        );
        if (!isValid) {
            return router.navigate([
                `/mip/investment-plan/${route.params['investmentPlanId']}/edit/scenario/${route.params['scenarioId']}/step/${MipScenarioStepNumber.INFORMATION_GENERAL}`
            ]);
        }
    }
    return true;
};
